import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Input, Modal, Select, Spin, Switch, message } from "antd";
import { DownOutlined, RightOutlined, SendOutlined } from "@ant-design/icons";

import {
  getCrmReportPreview,
  getOfficeReportSchedule,
  saveOfficeReportSchedule,
  sendCrmReport,
} from "./controller";
import "./officeReportPanel.css";

/**
 * One office's value report: what it says, whether it is going out, and to whom.
 *
 * Mounted twice — under the office selector on CRM Analytics, and as the office
 * drawer's Reports tab — because they are the same question from two directions.
 * The cross-office rollout table in CRM Settings answers a different one (which
 * clinics are set up) and stays where it is.
 *
 * READ ORDER IS THE DESIGN. Three questions in the order someone actually asks
 * them: is this reaching the clinic, what does it say, and what exactly do they
 * receive. Settings come last — nobody opens this to look at a cadence dropdown.
 * The first version led with the controls and left the answer in a thin banner
 * between them, which is why nothing could be read at a glance.
 *
 * The FIGURES are rendered natively from `report`, the structured payload the
 * preview endpoint already returns, instead of by reading the email's own markup
 * — that markup is shaped for Outlook (tables, 33% columns, 8pt type) and is
 * near-unreadable in a narrow drawer. Both come from one compute_report call, so
 * they cannot disagree.
 *
 * The EMAIL is still the server's HTML, byte for byte what the clinic receives,
 * behind a disclosure. Re-rendering that here would give a preview that drifts
 * from the send, which is worse than no preview.
 */

/** Mirrors office_reports._fmt_hours, so the app and the email agree. */
function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${Number(h.toFixed(1))} hrs`;
  return `${(h / 24).toFixed(1)} days`;
}

function fmtRange(report) {
  if (!report || !report.start || !report.end) return "";
  const opts = { month: "short", day: "numeric" };
  return `${new Date(report.start).toLocaleDateString([], opts)} – `
    + `${new Date(report.end).toLocaleDateString([], opts)}`;
}

function Tile({ label, value, note }) {
  return (
    <div className="orp-tile">
      <div className="orp-tile-label">{label}</div>
      <div className="orp-tile-num">{value}</div>
      {note ? <div className="orp-tile-note">{note}</div> : null}
    </div>
  );
}

export default function OfficeReportPanel({ officeId, compact }) {
  const [schedule, setSchedule] = useState(null);
  const [preview, setPreview] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [intro, setIntro] = useState("");
  const [showEmail, setShowEmail] = useState(false);

  const load = useCallback(
    async function (oid, per) {
      if (!oid) { setSchedule(null); setPreview(null); setLoading(false); return; }
      setLoading(true);
      const [s, p] = await Promise.all([
        getOfficeReportSchedule(oid),
        getCrmReportPreview(oid, per),
      ]);
      const sOk = s.status >= 200 && s.status < 300 && s.json;
      const pOk = p.status >= 200 && p.status < 300 && p.json;
      // An error is not "nothing configured" — see the empty-state lesson in
      // CrmSettings. Never state something we haven't established.
      setFailed(!sOk);
      setSchedule(sOk ? s.json : null);
      if (sOk) setIntro(s.json.intro || "");
      setPreview(pOk ? p.json : null);
      setLoading(false);
    },
    [],
  );

  useEffect(function () { load(officeId, period); }, [officeId, period, load]);

  async function patch(body) {
    const res = await saveOfficeReportSchedule(officeId, body);
    if (res.status >= 200 && res.status < 300) {
      setSchedule(res.json);
    } else {
      message.error((res.json && res.json.detail) || "Couldn't save that.");
      load(officeId, period);
    }
  }

  function confirmSend() {
    const to = (schedule && schedule.recipients) || [];
    Modal.confirm({
      // Mounted inside the office drawer too, which sits at z-index 10001.
      zIndex: 10050,
      title: "Send this report now?",
      // Named, not counted — this is the last moment before a customer receives
      // a claim about our own response times.
      content: (
        <div>
          <p style={{ marginBottom: 6 }}>
            From <strong>{schedule.sender_email}</strong> to:
          </p>
          <ul className="orp-tolist">
            {to.map((r) => (
              <li key={r.email}>{r.name ? `${r.name} · ${r.email}` : r.email}</li>
            ))}
          </ul>
        </div>
      ),
      okText: "Send",
      onOk: doSend,
    });
  }

  async function doSend() {
    setSending(true);
    try {
      const res = await sendCrmReport({ office_id: officeId, period });
      if (res.status >= 200 && res.status < 300) {
        message.success("Report sent.");
        load(officeId, period);
      } else {
        message.error((res.json && res.json.detail) || "Couldn't send the report.");
      }
    } finally {
      setSending(false);
    }
  }

  if (!officeId) {
    return (
      <div className="orp-blank">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Choose a clinic." />
      </div>
    );
  }

  if (loading) return <div className="orp-blank"><Spin /></div>;

  if (failed) {
    return (
      <div className="orp-blank">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <>
              <div>Couldn&rsquo;t load this clinic&rsquo;s report settings.</div>
              <div className="crm-sub" style={{ marginTop: 4 }}>
                The server returned an error — this is not the same as nothing
                being configured.
              </div>
            </>
          }
        >
          <Button size="small" onClick={() => load(officeId, period)}>Try again</Button>
        </Empty>
      </div>
    );
  }

  const blocked = schedule && schedule.blocked_reason;
  const recipients = (schedule && schedule.recipients) || [];
  const report = (preview && preview.report) || null;
  const v = (report && report.volume) || {};
  const o = (report && report.outcomes) || {};
  const t = (report && report.turnaround) || {};
  const h = (report && report.handling) || {};
  const lastSent = schedule && schedule.last_sent_at
    ? new Date(schedule.last_sent_at).toLocaleDateString() : null;

  return (
    <div className={"orp" + (compact ? " is-compact" : "")}>
      {/* 1 — is this reaching the clinic? The first question, so the first row. */}
      <div className={"orp-status" + (blocked ? " is-blocked" : "")}>
        <span className="orp-dot" />
        <div className="orp-status-text">
          {blocked ? (
            <><strong>Not sending.</strong> {blocked}</>
          ) : (
            <>
              <strong>
                {schedule.enabled
                  ? `Sending ${schedule.cadence === "weekly" ? "weekly" : "monthly"}`
                  : "Ready — but not scheduled"}
              </strong>
              <span className="orp-route">
                {schedule.sender_email} → {recipients.map((r) => r.email).join(", ")}
              </span>
              {lastSent ? <span className="orp-lastsent">Last sent {lastSent}</span> : null}
            </>
          )}
        </div>
        <Button
          type="primary"
          size="small"
          icon={<SendOutlined />}
          loading={sending}
          disabled={!!blocked}
          onClick={confirmSend}
        >
          Send now
        </Button>
      </div>

      {/* 2 — what does it say? In the app's own type, not the email's. */}
      <div className="orp-figures-head">
        <Select
          size="small"
          value={period}
          onChange={setPeriod}
          options={[
            { value: "monthly", label: "This month" },
            { value: "weekly", label: "This week" },
          ]}
          dropdownStyle={{ zIndex: 10050 }}
          style={{ width: 132 }}
        />
        <span className="orp-range">{fmtRange(report)}</span>
      </div>

      {report ? (
        <>
          {/* Six tiles, mirroring render_report_html's own 2x3 grid — labels and
              notes included. Three of these used to be demoted to a footnote
              line, which was an arbitrary cut: urgency, who did the work and the
              automation rate are the value story, not detail, and the email the
              clinic reads gives all six equal weight. Previewing something in a
              different hierarchy than it is sent in is its own small lie. */}
          <div className="orp-tiles">
            <Tile
              label="Requests received"
              value={v.requested != null ? v.requested : "—"}
              note={
                `${v.breakdowns || 0} breakdowns · ${v.estimates || 0} estimates`
                // Older reports predate claim follow-ups; only say it when it is counted.
                + (v.claim_followups != null ? ` · ${v.claim_followups} claim follow-ups` : "")
              }
            />
            <Tile
              label="Delivered"
              value={o.delivered != null ? o.delivered : "—"}
              note={o.outstanding ? `${o.outstanding} still open` : "all caught up"}
            />
            <Tile
              label="Typical turnaround"
              value={fmtHours(t.all && t.all.median_hours)}
              note={t.all && t.all.average_hours != null
                ? `average ${fmtHours(t.all.average_hours)}`
                : "nothing delivered yet"}
            />
            <Tile
              label="Urgent requests"
              value={v.urgent != null ? v.urgent : "—"}
              note={t.urgent && t.urgent.count
                ? `typically ${fmtHours(t.urgent.median_hours)}`
                : "none this period"}
            />
            <Tile
              label="Handled by our team"
              value={h.by_team != null ? h.by_team : "—"}
              note={`${h.by_agent || 0} completed automatically`}
            />
            <Tile
              label="Automated first time"
              value={h.first_time_right_pct != null ? `${h.first_time_right_pct}%` : "—"}
              note={h.agent_attempts
                ? `${h.handed_over || 0} passed to our team`
                : "no automated attempts"}
            />
          </div>

          {/* The chase prompt sits below the grid in the email too — it is a
              different kind of fact from a period metric. */}
          {o.outstanding && o.oldest_outstanding_hours != null ? (
            <div className="orp-chase">
              The oldest open request has been with us{" "}
              <b>{fmtHours(o.oldest_outstanding_hours)}</b>.
            </div>
          ) : null}
        </>
      ) : (
        <div className="orp-blank">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                 description="Couldn't build a preview for this clinic." />
        </div>
      )}

      {/* 3 — the email itself, on request. Server-rendered, so exactly what the
             clinic gets; folded away because it confirms rather than informs. */}
      <button type="button" className="orp-disclose" onClick={() => setShowEmail((x) => !x)}>
        {showEmail ? <DownOutlined /> : <RightOutlined />}
        <span className="orp-disclose-label">What the clinic receives</span>
        {preview ? <span className="orp-disclose-sub">{preview.subject}</span> : null}
      </button>
      {showEmail && preview ? (
        <div className="orp-paper" dangerouslySetInnerHTML={{ __html: preview.html }} />
      ) : null}

      {/* 4 — settings last. */}
      <div className="orp-settings">
        <div className="orp-settings-head">Delivery</div>
        <div className="orp-setting">
          <span>Send automatically</span>
          <Switch
            size="small"
            checked={!!(schedule && schedule.enabled)}
            onChange={(next) => patch({ enabled: next })}
          />
        </div>
        <div className="orp-setting">
          <span>Cadence</span>
          <Select
            size="small"
            value={(schedule && schedule.cadence) || "monthly"}
            onChange={(next) => patch({ cadence: next })}
            options={[
              { value: "monthly", label: "Monthly" },
              { value: "weekly", label: "Weekly" },
            ]}
            dropdownStyle={{ zIndex: 10050 }}
            style={{ width: 120 }}
          />
        </div>
        {schedule && schedule.ftc_options && schedule.ftc_options.length > 1 ? (
          <div className="orp-setting">
            <span>Signed by</span>
            <Select
              size="small"
              value={schedule.sender_id}
              onChange={(next) => patch({ sender_id: next || null })}
              options={schedule.ftc_options.map((x) => ({ value: x.id, label: x.email }))}
              dropdownStyle={{ zIndex: 10050 }}
              style={{ width: 220 }}
            />
          </div>
        ) : null}
        <div className="orp-setting is-stacked">
          <span>Opening note</span>
          <Input.TextArea
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            onBlur={(e) => patch({ intro: e.target.value })}
            placeholder="An optional line above the figures…"
            autoSize={{ minRows: 2, maxRows: 5 }}
          />
        </div>
      </div>
    </div>
  );
}
